import { emptyDefinition, newId } from '../../shared/template-builder'

/**
 * "Yeni" derken açılan hazır rapor çıktı şablonu.
 *
 * Amaç boş sayfayla başlamamak: tipik bir radyoloji raporunun iskeleti (antet, hasta/çekim bilgi
 * tablosu, dört rapor bölümü, imza, sayfa numarası) hazır gelir; kullanıcı üzerinde düzenler.
 *
 * Alan adları `ReportPlaceholders` katalogundakilerle birebir aynı olmalı — buraya katalogda olmayan
 * bir token yazılırsa builder "tanınmayan alan" uyarısı verir ve çıktıda boş kalır.
 */

/** Çok dilli metin: bilinen çeviriler doldurulur, kalan diller Türkçe metne düşer. */
const L = (texts, languages) =>
    languages.reduce((acc, lang) => ({ ...acc, [lang]: texts[lang] || texts.tr || '' }), {})

const cell = (texts, languages, extra = {}) => ({
    html: L(texts, languages),
    colSpan: 1,
    rowSpan: 1,
    ...extra,
})

/** Başlık + içerik alanı ikilisi — dört rapor bölümü için tekrar eden kalıp. */
const section = (title, token, languages) => ({
    id: newId(),
    type: 'text',
    align: 'left',
    html: L(
        {
            tr: `<p><strong>${title.tr}</strong></p><p>${token}</p>`,
            en: `<p><strong>${title.en}</strong></p><p>${token}</p>`,
        },
        languages
    ),
})

export function defaultReportOutputTemplate(languages, defaultLanguage) {
    const langs = languages && languages.length ? languages : ['tr']
    const base = emptyDefinition(langs, defaultLanguage || langs[0])

    return {
        ...base,
        name: '',
        header: {
            default: {
                blocks: [
                    {
                        id: newId(),
                        type: 'text',
                        align: 'center',
                        html: L(
                            {
                                tr: '<p><strong>{{hospitalOfficialName}}</strong></p>',
                                en: '<p><strong>{{hospitalOfficialName}}</strong></p>',
                            },
                            langs
                        ),
                    },
                ],
            },
        },
        footer: {
            default: {
                blocks: [
                    {
                        id: newId(),
                        type: 'text',
                        align: 'center',
                        html: L(
                            { tr: '<p>{{hospitalName}}</p>', en: '<p>{{hospitalName}}</p>' },
                            langs
                        ),
                    },
                ],
            },
            pageNumber: { enabled: true, format: '{PAGE} / {PAGES}', align: 'center' },
        },
        body: [
            {
                id: newId(),
                type: 'text',
                align: 'center',
                html: L(
                    {
                        tr: '<p><strong>{{requestDisplayName}}</strong></p>',
                        en: '<p><strong>{{requestDisplayName}}</strong></p>',
                    },
                    langs
                ),
            },

            {
                id: newId(),
                type: 'table',
                borders: true,
                headerBold: false,
                columns: [{ widthPercent: 50 }, { widthPercent: 50 }],
                rows: [
                    [
                        cell({ tr: 'Hasta Adı: {{patientName}}', en: 'Patient: {{patientName}}' }, langs),
                        cell({ tr: 'Çekim Tarihi: {{studyDate}}', en: 'Study Date: {{studyDate}}' }, langs),
                    ],
                    [
                        cell({ tr: 'Hasta Kimliği: {{patientId}}', en: 'Patient ID: {{patientId}}' }, langs),
                        cell({ tr: 'Acc No: {{accessionNumber}}', en: 'Acc No: {{accessionNumber}}' }, langs),
                    ],
                    [
                        cell({ tr: 'Doğum Tarihi: {{patientBirthdate}}', en: 'Birthdate: {{patientBirthdate}}' }, langs),
                        cell({ tr: 'Cinsiyet: {{gender}}', en: 'Gender: {{gender}}' }, langs),
                    ],
                    [
                        cell({ tr: 'Modalite: {{modality}}', en: 'Modality: {{modality}}' }, langs),
                        cell({ tr: 'Rapor Tarihi: {{reportDate}}', en: 'Report Date: {{reportDate}}' }, langs),
                    ],
                ],
            },

            { id: newId(), type: 'spacer', size: 1 },

            section({ tr: 'TEKNİK', en: 'TECHNIQUE' }, '{{ECAHUAQD}}', langs),
            section({ tr: 'KARŞILAŞTIRMA', en: 'COMPARISON' }, '{{QWDGGXPD}}', langs),
            section({ tr: 'BULGULAR', en: 'FINDINGS' }, '{{AZCMSRCT}}', langs),
            section({ tr: 'SONUÇ VE ÖNERİLER', en: 'CONCLUSION' }, '{{SUFHFZNM}}', langs),

            { id: newId(), type: 'spacer', size: 2 },

            {
                id: newId(),
                type: 'signature',
                slots: [
                    {
                        title: L({ tr: 'Raporlayan Hekim', en: 'Reporting Physician' }, langs),
                        nameKey: '{{doctorName}}',
                        signKey: '{{doctorSign}}',
                        dateKey: '{{reportConfirmDate}}',
                    },
                ],
            },
        ],
    }
}

export default defaultReportOutputTemplate
